import { useCallback, useEffect, useMemo, useState } from 'react'
import { FetchState } from 'shared/api'
import { flatten } from 'shared/util'
import { useKeyBind } from 'shared/keyBind'

import { useSearch } from 'features/search/context'
import { SisetuResult, useGetSisetu } from 'interface/sisetu'
import { KojinResult } from 'interface/kojin'
import { ArticleResult } from 'interface/article'
import { TrialResult } from 'interface/trial'
import { ResearchProjectResult } from 'interface/research'

import { useSisetuPreviewDialog } from 'features/sisetu/dialog/preview/hooks'
import { useKojinPreviewDialog } from 'features/kojin/dialog/preview/hooks'
import { useArticlePreviewDialog } from 'features/article/dialog/preview/hooks'
import { useTrialPreviewDialog } from 'features/trial/dialog/preview/hooks'
import { useResearchPreviewDialog } from 'features/research/dialog/preview/hooks'

type useSisetuPreviewProps = {
  sisetuList?: SisetuResult[]
	defaultSisetu?: SisetuResult | null
  defaultSisetuCode?: string | null
	onSisetuOpenReply?: (sisetuCode: string | null) => void
	onKojinOpenReply?: (kojinCode: string | null) => void
	onAuthorOpenReply?: (authorId: number | null) => void
	onClose?: () => void
}
export const useSisetuPreview = ({
	sisetuList,
	defaultSisetu,
	defaultSisetuCode,
	onSisetuOpenReply,
	onKojinOpenReply,
	onAuthorOpenReply,
	onClose
}: useSisetuPreviewProps) => {
	const search = useSearch()

	const [sisetuCode, setSisetuCode] = useState<string | null>(defaultSisetuCode ?? defaultSisetu?.sisetuCode ?? null)

	const {
		current,
		prev,
		next,
	} = useMemo(
		() => {
			const index = (
				sisetuCode ? (sisetuList?.findIndex(sisetu => sisetu.sisetuCode === sisetuCode) ?? -1) :
				-1
			)
			return {
				current: (sisetuList && index >= 0) ? sisetuList[index] : null,
				next: (sisetuList && index >= 0 && index < (sisetuList.length - 1)) ? sisetuList[index + 1] : null,
				prev: (sisetuList && index > 0) ? sisetuList[index - 1] : null
			}
		},
		[sisetuList, sisetuCode]
	)

	const sisetuState: FetchState<SisetuResult> = useGetSisetu(
		search?.searchId,
		sisetuCode,
		{
			defaultSisetu: current ?? defaultSisetu ?? undefined,
			updateTime: search?.updateTime ?? undefined
		}
	)
  const sisetu = useMemo(() => sisetuState.data, [sisetuState])

	useEffect(
		() => {
			if (sisetu) {
				setSisetuCode(sisetu.sisetuCode ?? null)
			}
		},
		[sisetu, setSisetuCode]
	)

	const {
		openSisetu, sisetuCode: selectedSisetuCode
	} = useSisetuPreviewDialog()
	const {
		openKojin, kojinCode: selectedKojinCode,
		openAuthor, authorId: selectedAuthorId,
		setKojinList, kojinList,
	} = useKojinPreviewDialog()
	const {
		openArticle, articleId: selectedArticleId,
		setArticleList, articleList
	} = useArticlePreviewDialog()
	const {
		openTrial, trialId: selectedTrialId,
		setTrialList, trialList
	} = useTrialPreviewDialog()
	const {
		openProject, projectKey: selectedProjectKey,
		setProjectList, projectList
	} = useResearchPreviewDialog()

	const closeModal = useCallback(
		(exclusive: 'sisetu' | 'kojin' | 'article' | 'trial' | 'project' | null = null) => {
			if (exclusive !== 'sisetu') {
				if (onSisetuOpenReply) {
					onSisetuOpenReply(null)
				} else {
					openSisetu(null)
				}
			}
			if (exclusive !== 'kojin') {
				if (onKojinOpenReply) {
					onKojinOpenReply(null)
				} else {
					openKojin(null)
				}
			}
			if (exclusive !== 'article') {
				openArticle(null)
			}
			if (exclusive !== 'trial') {
				openTrial(null)
			}
			if (exclusive !== 'project') {
				openProject(null)
			}
		},
		[onSisetuOpenReply, openSisetu, onKojinOpenReply, openKojin, openArticle, openTrial, openProject]
	)

	const isShowModal = useMemo(
		() => !!(selectedSisetuCode || selectedKojinCode || selectedAuthorId || selectedArticleId || selectedTrialId || selectedProjectKey),
		[selectedSisetuCode, selectedKojinCode, selectedAuthorId, selectedArticleId, selectedTrialId, selectedProjectKey]
	)

	const openPrev = useCallback(
		() => {
			if (isShowModal) return
			if (prev?.sisetuCode) setSisetuCode(prev.sisetuCode)
		},
		[prev, setSisetuCode, isShowModal]
	)
	const openNext = useCallback(
		() => {
			if (isShowModal) return
			if (next?.sisetuCode) setSisetuCode(next.sisetuCode)
		},
		[next, setSisetuCode, isShowModal]
	)
	const onSisetuOpen = useCallback(
		(otherSisetuCode: string | null) => {
			closeModal('sisetu')
			if (onSisetuOpenReply) {
				onSisetuOpenReply(otherSisetuCode)
			} else {
				if (otherSisetuCode === sisetuCode) return
				setSisetuCode(otherSisetuCode)
			}
		},
		[closeModal, onSisetuOpenReply, sisetuCode]
	)
	const onKojinOpen = useCallback(
		(otherKojinCode: string | null, list?: KojinResult[]) => {
			closeModal('kojin')
			if (onKojinOpenReply) {
				onKojinOpenReply(otherKojinCode)
			} else {
				setKojinList(list ?? [])
				openKojin(otherKojinCode)
			}
		},
		[closeModal, onKojinOpenReply, setKojinList, openKojin]
	)
	const onAuthorOpen = useCallback(
		(otherAuthorId: number | null) => {
			closeModal('kojin')
			if (onAuthorOpenReply) {
				onAuthorOpenReply(otherAuthorId)
			} else {
				openAuthor(otherAuthorId)
			}
		},
		[closeModal, onAuthorOpenReply, openAuthor]
	)
	const onArticleOpen = useCallback(
		(article: ArticleResult | null, list?: ArticleResult[]) => {
			closeModal('article')
			setArticleList(list ?? [])
			openArticle(article?.articleId ?? null)
		},
		[closeModal, setArticleList, openArticle]
	)
	const onTrialOpen = useCallback(
		(trial: TrialResult | null, list?: TrialResult[]) => {
			closeModal('trial')
			setTrialList(list ?? [])
			openTrial(trial?.trialId ?? null)
		},
		[closeModal, setTrialList, openTrial]
	)
	const onResearchOpen = useCallback(
		(project: ResearchProjectResult | null, list?: ResearchProjectResult[]) => {
			closeModal('project')
			setProjectList(list ?? [])
			openProject(project?.projectKey ?? null)
		},
		[closeModal, setProjectList, openProject]
	)
  useKeyBind({ key: "PageUp", onKeyDown: openPrev })
  useKeyBind({ key: "PageDown", onKeyDown: openNext })

	return {
		loading: sisetuState.loading,
		error: flatten([sisetuState.error]),
		sisetuList, sisetuCode, sisetu,
		hasPrev: prev !== null ? true : false,
		hasNext: next !== null ? true : false,
		closeModal,
		onSisetuOpen, selectedSisetuCode,
		onKojinOpen, selectedKojinCode, kojinList,
		onAuthorOpen, selectedAuthorId,
		onArticleOpen, selectedArticleId, articleList,
		onTrialOpen, selectedTrialId, trialList,
		onResearchOpen, selectedProjectKey, projectList,
		openPrev, openNext,
		onClose
	}
}
